import { useState } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { BellRing, Loader2 } from "lucide-react";

/**
 * Small action button "Relancer" — sends a reminder push + internal notif to
 * the target client only. Never notifies the admin who triggers it.
 *
 * Props:
 *   clientId (required) — string
 *   targetType (optional) — "document" | "message" | "demande" | "general"
 *   targetId (optional) — id of the comm/doc/question being reminded about
 *   variant (optional) — "icon" | "small" | "default" (size/look)
 *   className — additional Tailwind classes
 *   children — custom button label (default: "Relancer")
 */
export default function RelanceButton({
  clientId,
  targetType = "general",
  targetId = null,
  variant = "default",
  className = "",
  children = "Relancer",
  onSent,
}) {
  const [busy, setBusy] = useState(false);

  const handleClick = async (e) => {
    e?.stopPropagation?.();
    if (busy || !clientId) return;
    setBusy(true);
    try {
      const r = await api.post("/relances", {
        client_id: clientId,
        target_type: targetType,
        target_id: targetId,
      });
      if (r.data?.ok) {
        toast.success(r.data.message || "Relance envoyée");
        onSent?.(r.data);
      } else {
        toast.error(r.data?.detail || "Échec de la relance");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur lors de la relance");
    } finally {
      setBusy(false);
    }
  };

  if (variant === "icon") {
    return (
      <button
        onClick={handleClick}
        disabled={busy || !clientId}
        title="Relancer le client"
        aria-label="Relancer le client"
        className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-amber-600 hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed transition active:scale-90 ${className}`}
        data-testid="relance-btn-icon"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />}
      </button>
    );
  }

  if (variant === "small") {
    return (
      <button
        onClick={handleClick}
        disabled={busy || !clientId}
        className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 disabled:opacity-50 disabled:cursor-not-allowed transition active:scale-95 ${className}`}
        data-testid="relance-btn-small"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellRing className="w-3 h-3" />}
        {busy ? "..." : children}
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={busy || !clientId}
      className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition active:scale-95 ${className}`}
      data-testid="relance-btn"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />}
      {busy ? "Envoi..." : children}
    </button>
  );
}
